import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Flex, Heading, Link, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getPublicCustomMetrics } from '../../api';
import { useColorModeValue } from '../../components/ui/color-mode';
import NotFound from '../NotFound';
import {
    formatMetricDate,
    formatMetricValue,
    PublicPageShell,
} from './PublicMetricsShared';
import { PublicMetricLink } from './PublicMetricLink';
import { SourceCell } from './SourceCell';

const PUBLIC_METRIC_CATEGORY_MIN_COUNT = 3;

const PUBLIC_METRIC_CATEGORIES = {
    subscribers: {
        h1: 'Subscriber KPI Data',
        description:
            'Browse filing-sourced subscriber and membership metrics with historical values, latest periods, and source filing links.',
        keywords: ['subscriber', 'subscribers', 'membership', 'memberships'],
    },
    users: {
        h1: 'User and Customer KPI Data',
        description:
            'Browse filing-sourced user, customer, client, and member metrics with historical values, latest periods, and source filing links.',
        keywords: ['user', 'users', 'customer', 'customers', 'client', 'clients', 'member', 'members'],
    },
    volume: {
        h1: 'Volume KPI Data',
        description:
            'Browse filing-sourced transaction, trip, and volume metrics with historical values, latest periods, and source filing links.',
        keywords: ['volume', 'volumes', 'trip', 'trips', 'transaction', 'transactions'],
    },
};

function categoryMatches(metric, category) {
    const haystack = ['metric_key', 'metric_slug', 'display_name', 'description']
        .map((key) => String(metric?.[key] || ''))
        .join(' ')
        .toLowerCase();
    return category.keywords.some((keyword) => haystack.includes(keyword));
}

function buildCategories(metrics) {
    return Object.entries(PUBLIC_METRIC_CATEGORIES)
        .flatMap(([slug, category]) => {
            const categoryMetrics = metrics.filter((metric) => categoryMatches(metric, category));
            return categoryMetrics.length >= PUBLIC_METRIC_CATEGORY_MIN_COUNT
                ? [
                      {
                          ...category,
                          slug,
                          url: `/metrics/${slug}`,
                          metrics: categoryMetrics,
                          metric_count: categoryMetrics.length,
                      },
                  ]
                : [];
        });
}

function MetricSummaryRow({ metric }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.600', 'gray.300');
    const latest = metric.latest_value || {};

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4}>
            <Stack gap={2}>
                <Box>
                    <Text display="block" fontSize="md" color={muted} fontWeight="semibold" lineHeight="1.2">
                        {metric.ticker}
                    </Text>
                    <Text display="block" fontSize="xl" fontWeight="semibold" lineHeight="1.25" mt={1}>
                        <PublicMetricLink metric={metric} />
                    </Text>
                </Box>
                <Box>
                    <Text display="block" fontSize="lg" fontWeight="bold" lineHeight="1.2">
                        {formatMetricValue(latest.value, latest.unit || metric.unit)}
                    </Text>
                    <Text display="block" fontSize="sm" color={muted} mt={1}>
                        {formatMetricDate(latest.period_end)}
                    </Text>
                </Box>
                <Box fontSize="sm">
                    <SourceCell value={latest} />
                </Box>
            </Stack>
            {metric.description && (
                <Text mt={3} color={muted} fontSize="sm" lineClamp={2}>
                    {metric.description}
                </Text>
            )}
        </Box>
    );
}

function PublicMetricsIndexPage() {
    const { categorySlug } = useParams();
    const [state, setState] = useState({ status: 'loading', payload: null, error: null });

    useEffect(() => {
        let cancelled = false;

        getPublicCustomMetrics()
            .then((payload) => {
                if (!cancelled) setState({ status: 'success', payload, error: null });
            })
            .catch((error) => {
                if (!cancelled) setState({ status: 'error', payload: null, error: error.message });
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const metrics = useMemo(() => state.payload?.metrics || [], [state.payload]);
    const categories = useMemo(() => buildCategories(metrics), [metrics]);
    const activeCategory = categorySlug
        ? categories.find((category) => category.slug === String(categorySlug).toLowerCase())
        : null;
    const visibleMetrics = activeCategory ? activeCategory.metrics : metrics;
    const tickers = useMemo(
        () => [
            ...new Set(
                metrics.flatMap((metric) => {
                    const ticker = String(metric.ticker || '').toUpperCase();
                    return ticker ? [ticker] : [];
                }),
            ),
        ].toSorted(),
        [metrics],
    );

    if (state.status === 'success' && categorySlug && !activeCategory) {
        return <NotFound />;
    }

    return (
        <PublicPageShell>
            <Stack gap={6}>
                <Box>
                    {categorySlug && (
                        <Link as={RouterLink} to="/metrics" color="blue.600" fontWeight="semibold">
                            All public KPI data
                        </Link>
                    )}
                    <Text mt={categorySlug ? 5 : 0} color="gray.500" fontWeight="semibold" textTransform="uppercase" fontSize="sm">
                        Filing-sourced metrics
                    </Text>
                    <Heading as="h1" size="2xl" mt={2}>
                        {activeCategory?.h1 || 'Public Company KPI Data'}
                    </Heading>
                    <Text mt={3} color="gray.600" maxW="820px">
                        {activeCategory?.description ||
                            'Browse historical operating metrics sourced from company filings. Each metric includes the latest value, historical data, and source filing links.'}
                    </Text>
                </Box>

                {state.status === 'loading' && (
                    <Flex align="center" gap={3}>
                        <Spinner size="sm" />
                        <Text>Loading public metrics…</Text>
                    </Flex>
                )}

                {state.status === 'error' && (
                    <Alert.Root status="error">
                        <Alert.Indicator />
                        <Alert.Content>{state.error || 'Unable to load public metrics.'}</Alert.Content>
                    </Alert.Root>
                )}

                {state.status === 'success' && (
                    <>
                        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                            <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
                                <Flex align="baseline" gap={2} wrap="wrap">
                                    <Text display="block" fontSize="2xl" fontWeight="bold" lineHeight="1">
                                        {visibleMetrics.length}
                                    </Text>
                                    <Text display="block" color="gray.600">public metrics</Text>
                                </Flex>
                            </Box>
                            <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
                                <Flex align="baseline" gap={2} wrap="wrap">
                                    <Text display="block" fontSize="2xl" fontWeight="bold" lineHeight="1">
                                        {categorySlug ? new Set(visibleMetrics.map((metric) => metric.ticker)).size : tickers.length}
                                    </Text>
                                    <Text display="block" color="gray.600">companies with public KPI data</Text>
                                </Flex>
                            </Box>
                        </SimpleGrid>

                        {!categorySlug && tickers.length > 0 && (
                            <Box>
                                <Heading as="h2" size="lg" mb={3}>
                                    Companies
                                </Heading>
                                <Flex gap={2} wrap="wrap">
                                    {tickers.map((ticker) => (
                                        <Link
                                            key={ticker}
                                            as={RouterLink}
                                            to={`/stocks/${ticker.toLowerCase()}/metrics`}
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            borderRadius="md"
                                            px={3}
                                            py={2}
                                            fontWeight="semibold"
                                            color="blue.600"
                                        >
                                            {ticker}
                                        </Link>
                                    ))}
                                </Flex>
                            </Box>
                        )}

                        {!categorySlug && categories.length > 0 && (
                            <Box>
                                <Heading as="h2" size="lg" mb={3}>
                                    Metric Categories
                                </Heading>
                                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                                    {categories.map((category) => (
                                        <Box key={category.slug} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
                                            <Link as={RouterLink} to={category.url} color="blue.600" fontWeight="semibold">
                                                {category.h1}
                                            </Link>
                                            <Text mt={2} color="gray.600" fontSize="sm">
                                                {category.description}
                                            </Text>
                                            <Text mt={2} color="gray.600" fontSize="sm">
                                                {category.metric_count} public metrics
                                            </Text>
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            </Box>
                        )}

                        <Box>
                            <Heading as="h2" size="lg" mb={3}>
                                {categorySlug ? 'Metrics' : 'All Public Metrics'}
                            </Heading>
                            {visibleMetrics.length > 0 ? (
                                <Stack gap={3}>
                                    {visibleMetrics.map((metric) => (
                                        <MetricSummaryRow
                                            key={`${metric.ticker}-${metric.metric_key || metric.metric_slug}`}
                                            metric={metric}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Text color="gray.600">No public metrics are available yet.</Text>
                            )}
                        </Box>
                    </>
                )}
            </Stack>
        </PublicPageShell>
    );
}

export default PublicMetricsIndexPage;
